import { useState } from "react";
import { Download } from "lucide-react";
import { PageHeader } from "../../../components/PortfolioPerformance/page-header";
import { Panel } from "../../../components/PortfolioPerformance/panel";
import { FilterBar, type FilterDef } from "../../../components/PortfolioPerformance/filter-bar";
import { ReportFrame } from "../../../components/PortfolioPerformance/report-frame";
import { Button } from "../../../ui/button";
import {
  ALL_INDUSTRIES_OPTION,
  OFFICE_OPTIONS,
  RATING_OPTIONS,
  PG_CORP_OPTIONS,
  ACTIVE_OPTIONS,
  TICKET_SIZE_OPTIONS,
  usePoolBuilder,
} from "../lib/api/snapshot";
import { useBorrowerIndustries } from "../lib/api/borrower-industries";

const POWER_BI_EMBED_URL =
  "https://app.powerbi.com/reportEmbed?reportId=c198c4d2-ca37-4fa0-9a52-4d4cb8048bb0&autoAuth=true&ctid=72ae424f-c879-46c9-8f54-01a7830d2faa";

export default function SnapshotPage() {
  const [industry, setIndustry] = useState<string>(ALL_INDUSTRIES_OPTION);
  const borrowerIndustries = useBorrowerIndustries().data ?? [];
  const [office, setOffice] = useState<string>(OFFICE_OPTIONS[0]);
  const [rating, setRating] = useState<string>(RATING_OPTIONS[0]);
  const [pgCorp, setPgCorp] = useState<string>(PG_CORP_OPTIONS[0]);
  const [active, setActive] = useState<string>(ACTIVE_OPTIONS[0]);
  const [ticketSize, setTicketSize] = useState<string>(TICKET_SIZE_OPTIONS[0]);

  const filters = { industry, office, rating, pgCorp, active, ticketSize };
  const pool = usePoolBuilder(filters);

  const filterDefs: FilterDef[] = [
    {
      id: "industry",
      label: "Industry",
      value: industry,
      options: [ALL_INDUSTRIES_OPTION, ...borrowerIndustries],
      onChange: setIndustry,
    },
    { id: "office", label: "Office", value: office, options: OFFICE_OPTIONS, onChange: setOffice },
    { id: "rating", label: "Rating", value: rating, options: RATING_OPTIONS, onChange: setRating },
    {
      id: "pgCorp",
      label: "PG / Corp",
      value: pgCorp,
      options: PG_CORP_OPTIONS,
      onChange: setPgCorp,
    },
    {
      id: "active",
      label: "Active / Inactive",
      value: active,
      options: ACTIVE_OPTIONS,
      onChange: setActive,
    },
    {
      id: "ticketSize",
      label: "Ticket Size",
      value: ticketSize,
      options: TICKET_SIZE_OPTIONS,
      onChange: setTicketSize,
    },
  ];

  return (
    <div>
      <PageHeader
        title="Snapshot"
        subtitle="Ad hoc Superset views, sliced and downloaded on demand, plus the existing Power BI product embedded, the catch-all outside the four lenses, same pattern as RBPE."
      />

      <Panel
        className="mt-8"
        title="Ad Hoc Pool Builder"
        description="Handpick a pool by industry, office, rating, PG/Corp, active/inactive, ticket-size band. Export summarized or underlying data"
      >
        <FilterBar filters={filterDefs} />

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
          {pool.isPending ? (
            <div className="flex gap-6">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Matched Contracts
                </p>
                <p className="mt-1 text-xl font-semibold tabular-nums text-foreground">…</p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Matched NI
                </p>
                <p className="mt-1 text-xl font-semibold tabular-nums text-foreground">…</p>
              </div>
            </div>
          ) : pool.data == null ? (
            <p className="text-sm text-muted-foreground">Not Available Yet.</p>
          ) : (
            <div className="flex gap-6">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Matched Contracts
                </p>
                <p className="mt-1 text-xl font-semibold tabular-nums text-foreground">
                  {pool.data.matchedContracts.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Matched NI
                </p>
                <p className="mt-1 text-xl font-semibold tabular-nums text-foreground">
                  {pool.data.matchedNI}
                </p>
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <Button variant="outline" className="gap-2">
              <Download className="size-4" />
              Export Summarized View
            </Button>
            <Button variant="outline" className="gap-2">
              <Download className="size-4" />
              Export Underlying Data
            </Button>
          </div>
        </div>
      </Panel>

      <div className="mt-6">
        {/* The Power BI report is embedded via iframe, not proxied through the portfolio-performance API. */}
        <ReportFrame
          src={POWER_BI_EMBED_URL}
          title="Portfolio Performance"
          caption="Multi-dimensional and time-series views the front-end doesn't need to replicate."
        />
      </div>
    </div>
  );
}
